export const DEFAULT_STARTUP_CALCULATOR_INPUTS = {
  goal: 200000,
  price: 15000,
  margin: 70,
  cogs: 2000,
  adsPer: 1500,
  tax: 6,
  bank: 2,
  rent: 50000,
  salarySelf: 80000,
  salaryStaff: 40000,
  marketing: 30000,
  services: 10000,
  other: 10000,
  invest: 500000,
};

export const STARTUP_CALCULATOR_FORMULA_STEPS = [
  {
    marker: '①',
    title: 'Средний чек и прибыль на продажу',
    formula: 'Средний чек = Выручка ÷ Продажи · Прибыль на продажу = (Выручка − Все расходы) ÷ Продажи',
    example: 'Пример: 350 000 ÷ 85 = <b>4 118 ₸</b> чек. (350 000 − 221 000) ÷ 85 = <b>1 518 ₸</b> прибыли с каждой продажи',
  },
  {
    marker: '②',
    title: 'CPS — сколько можно потратить на привлечение одной продажи',
    formula: 'CPS = Средний чек × Желаемая маржинальность%',
    example: 'Пример: 4 118 × 7% = <b>288 ₸</b> — максимум на маркетинг чтобы остаться с нужной маржой',
  },
  {
    marker: '③',
    title: 'CPL — стоимость лида и CPC — стоимость клика',
    formula: 'CPL = CPS × CR2 · CPC = CPL × CR1',
    example: 'Пример: 288 × 50% = <b>144 ₸</b> за лид. 144 × 5% = <b>7 ₸</b> за клик — столько может стоить клик чтобы экономика сходилась',
  },
  {
    marker: '④',
    title: 'План на следующий месяц',
    formula: 'План продаж = (Продажи ÷ Период) × 30 × (1 + Рост%) · Бюджет = План × CPL ÷ CR2',
    example: 'Пример: (85 ÷ 45) × 30 × 1.25 = <b>71 продажа</b>. 71 × 144 ÷ 50% = <b>20 448 ₸</b> рекламного бюджета',
  },
];

export const clampNumber = (value, min, max = Number.MAX_SAFE_INTEGER) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return min;
  }

  return Math.min(max, Math.max(min, parsed));
};

export const formatMoney = (value, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${Math.round(value).toLocaleString(locale)} ₸`;
};

export const formatQty = (value, unit, locale = 'ru-RU') => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${Math.round(value).toLocaleString(locale)} ${unit}`;
};

export const formatPercent = (value, digits = 1) => {
  if (!Number.isFinite(value)) {
    return '—';
  }

  return `${value.toFixed(digits)}%`;
};

export const calcProfitPer = (price, marginPct, adsPerSale, taxPct, bankPct) => {
  const grossPer = (price * marginPct) / 100;
  const taxRub = (price * taxPct) / 100;
  const bankRub = (price * bankPct) / 100;

  return grossPer - adsPerSale - taxRub - bankRub;
};

const buildScenarioPrices = (price) => {
  const raw = [
    Math.round((price * 0.5) / 1000) * 1000 || Math.round(price * 0.5),
    Math.round((price * 0.75) / 500) * 500 || Math.round(price * 0.75),
    price,
    Math.round((price * 1.25) / 500) * 500 || Math.round(price * 1.25),
    Math.round((price * 1.5) / 1000) * 1000 || Math.round(price * 1.5),
  ];

  return raw.filter((value, index, array) => array.indexOf(value) === index && value > 0);
};

export const calculateStartupCalculatorMetrics = (inputs) => {
  const goal = clampNumber(inputs.goal, 0);
  const price = clampNumber(inputs.price, 1);
  const marginPct = clampNumber(inputs.margin, 1, 100);
  const adsPerSale = clampNumber(inputs.adsPer, 0);
  const taxPct = clampNumber(inputs.tax, 0, 50);
  const bankPct = clampNumber(inputs.bank, 0, 10);
  const rent = clampNumber(inputs.rent, 0);
  const salarySelf = clampNumber(inputs.salarySelf, 0);
  const salaryStaff = clampNumber(inputs.salaryStaff, 0);
  const marketing = clampNumber(inputs.marketing, 0);
  const services = clampNumber(inputs.services, 0);
  const other = clampNumber(inputs.other, 0);
  const invest = clampNumber(inputs.invest, 0);

  const taxRub = (price * taxPct) / 100;
  const bankRub = (price * bankPct) / 100;
  const grossPer = (price * marginPct) / 100;
  const varPer = adsPerSale + taxRub + bankRub;
  const realPer = grossPer - varPer;
  const realMargin = price > 0 ? (realPer / price) * 100 : 0;

  const fixed = rent + salarySelf + salaryStaff + marketing + services + other;
  const bepSales = realPer > 0 ? Math.ceil(fixed / realPer) : Infinity;
  const bepRub = Number.isFinite(bepSales) ? bepSales * price : NaN;

  const salesNeeded = realPer > 0 ? Math.ceil((fixed + goal) / realPer) : Infinity;
  const revenueNeeded = Number.isFinite(salesNeeded) ? salesNeeded * price : NaN;
  const salesDaily = Number.isFinite(salesNeeded) ? salesNeeded / 30 : NaN;

  const coeffPct = (marginPct - taxPct - bankPct) / 100;
  const minCheck =
    coeffPct > 0 && salesNeeded > 0 ? (fixed / salesNeeded + adsPerSale) / coeffPct : 0;

  const safety =
    salesNeeded > 0 && Number.isFinite(bepSales)
      ? ((salesNeeded - bepSales) / salesNeeded) * 100
      : 0;

  const payback = goal > 0 ? invest / goal : NaN;
  const paybackOpt = goal > 0 ? invest / (goal * 1.5) : NaN;
  const totalCosts = fixed + (Number.isFinite(salesNeeded) ? salesNeeded * varPer : 0);

  const scenarios = buildScenarioPrices(price).map((scenarioPrice) => {
    const profitPer = calcProfitPer(scenarioPrice, marginPct, adsPerSale, taxPct, bankPct);
    const bepQty = profitPer > 0 ? Math.ceil(fixed / profitPer) : null;
    const goalQty = profitPer > 0 ? Math.ceil((fixed + goal) / profitPer) : null;
    const revenue = goalQty ? goalQty * scenarioPrice : null;

    let tone = 'pos';
    if (profitPer < 0) {
      tone = 'neg';
    } else if (profitPer < realPer * 0.5) {
      tone = 'warn';
    }

    return {
      price: scenarioPrice,
      profitPer,
      bepQty,
      goalQty,
      revenue,
      isCurrent: scenarioPrice === price,
      tone,
    };
  });

  return {
    goal,
    price,
    grossPer,
    varPer,
    realPer,
    realMargin,
    fixed,
    totalCosts,
    bepSales,
    bepRub,
    salesNeeded,
    revenueNeeded,
    salesDaily,
    minCheck,
    safety,
    payback,
    paybackOpt,
    invest,
    scenarios,
  };
};

export const getInsightState = (metrics) => {
  const {
    realPer,
    realMargin,
    bepSales,
    goal,
    salesNeeded,
    salesDaily,
    safety,
  } = metrics;

  if (realPer <= 0) {
    return {
      variant: 'bad',
      message: 'Бизнес убыточен при текущих параметрах: расходы <b>{totalCost}</b> превышают выручку <b>{revenue}</b>. Нужно снизить себестоимость или поднять цену.',
      vars: { realPer },
    };
  }

  if (realMargin < 20) {
    return {
      variant: 'bad',
      message: 'Реальная маржинальность <b>{realMargin}</b> — очень низкая. Точка безубыточности <b>{bepSales} шт</b>. Небольшой рост расходов или падение продаж переведёт бизнес в минус.',
      vars: { realMargin, bepSales },
    };
  }

  if (!Number.isFinite(salesNeeded) || salesNeeded > 500) {
    return {
      variant: 'warn',
      message: 'Для достижения прибыли <b>{goal}</b> нужно <b>{salesNeeded}</b> в месяц. Рассмотрите повышение чека или снижение постоянных расходов.',
      vars: { goal, salesNeeded },
    };
  }

  if (safety > 40) {
    return {
      variant: 'good',
      message: 'Хорошая модель. Запас прочности <b>{safety}</b> — даже при падении продаж на треть бизнес остаётся в плюсе. Для прибыли <b>{goal}</b> нужно <b>{salesNeeded} продаж</b> ({salesDaily}/день).',
      vars: { safety, goal, salesNeeded, salesDaily },
    };
  }

  return {
    variant: 'warn',
    message: 'Для прибыли <b>{goal}</b> нужно <b>{salesNeeded} продаж</b> в месяц ({salesDaily}/день). Точка безубыточности <b>{bepSales} шт</b>. Запас прочности <b>{safety}</b> — есть риск при просадке продаж.',
    vars: { goal, salesNeeded, salesDaily, bepSales, safety },
  };
};
